import { RequiredLocalizedNameSchema, isVocabId } from '@dl/shared';
import { z } from 'zod';

import {
  AnchorEventSchema,
  CyclesSchema,
  EraSchema,
  FormatterRefSchema,
  LeapRuleSchema,
  PluginSpecSchema,
  isCycleBag,
  type Calendar,
  type CycleBag,
  type Cycles,
  type LabelSpec,
  type LegacyCycles,
  type LocalizedString,
  type MonthDefinition,
} from './types.js';

/**
 * `CalendarDefinition` — the pack-extractable DEFINITION form of a
 * calendar (#177, ADR-0004 §1/§4).
 *
 * A calendar is the #3 bucket of the fact / setting / definition model:
 * reusable schema, op-log meta-ops (`pack.define_calendar`), foldable
 * into the registry, extractable to packs. It is NOT a lore entity and
 * NOT a setting — the campaign's `canonical_calendar` *choice* is a
 * separate per-campaign pointer *setting* (config-ops, #180) holding this
 * definition's `id`.
 *
 * Identity is decoupled from naming (ADR-0004 §1):
 *
 *   - `id` is an **opaque, immutable, minted** `cal_<uuidv4>` handle
 *     (`mintVocabId('calendar')`), never user-facing, never typed by a
 *     human or the LLM.
 *   - `name` is a **mandatory** `LocalizedString` display name (at least
 *     the default locale present); names are freely duplicable.
 *
 * Structurally a `CalendarDefinition` is a superset of the runtime
 * `Calendar` (it adds the opaque-id + mandatory-name constraints and
 * renames `label` → `name`). The canonical day-count engine consumes a
 * `Calendar`; convert a definition with `asCalendar()`.
 *
 * This schema is the `@dl/calendar` chokepoint that re-validates a
 * calendar definition folded out of the op log by `@dl/shared`'s
 * `foldPackOps` — `@dl/shared` only validates the opaque-id + name
 * envelope (it cannot import this structural schema without forming a
 * `@dl/shared` ↔ `@dl/calendar` cycle).
 */
/** Identity envelope shared by both calendar modes (§3). */
const calendarIdentityShape = {
  /** Opaque, immutable, minted `cal_<uuidv4>` id (ADR-0004 §1). */
  id: z.string().refine((s) => isVocabId('calendar', s), {
    message: 'calendar id must be a minted opaque id (cal_<uuidv4>) — see mintVocabId',
  }),
  /** **Mandatory** localized display name (ADR-0004 §1). */
  name: RequiredLocalizedNameSchema,
  /**
   * Optional display escape hatch (§3, slice C2 / #243). A per-calendar
   * built-in formatter that renders the canonical day directly, bypassing
   * cycle decomposition (Stardate, 40K Imperial Dating). v1 is a closed
   * enum of built-ins (no arbitrary JS — that waits for the plugin runtime);
   * an unknown ref degrades to the structured formatter with a notice. The
   * override is **display-only** — a structured calendar carrying one still
   * parses/compares via its cycles. Carried through to the runtime
   * `Calendar` in `asCalendar`.
   */
  formatter_override: FormatterRefSchema.optional(),
} as const;

/**
 * A `structured`-mode calendar definition (§3) — the common path. The
 * engine parses/formats/reasons over it from the cycle/era/leap data.
 * `mode` is `.default('structured')` so a definition authored before #242
 * (no `mode` key) folds to `structured` and is byte-identical; the
 * structured fields stay REQUIRED.
 */
export const StructuredCalendarDefinitionSchema = z
  .object({
    ...calendarIdentityShape,
    mode: z.literal('structured').default('structured'),
    cycles: CyclesSchema,
    eras: z.array(EraSchema).min(1).readonly(),
    leap_rule: LeapRuleSchema,
    anchor_canonical_day: z.number().int(),
    anchor: AnchorEventSchema.optional(),
  })
  .strict();

/**
 * A `plugin`-mode calendar definition (§3, slice C1 / #242). Declares a
 * `plugin { module_id, capabilities }` body and OMITS the structured
 * cycle/era/leap fields (the engine never decomposes it). It LOADS (its
 * pack's vocabulary stays available) but does not render — calendar-
 * dependent surfaces degrade via {@link isPluginCalendar}. **No plugin code
 * executes in this slice.** `mode` is a required literal here (a plugin
 * definition opts in explicitly; the default is `structured`).
 */
export const PluginCalendarDefinitionSchema = z
  .object({
    ...calendarIdentityShape,
    mode: z.literal('plugin'),
    /**
     * The plugin module reference: `{ module_id, capabilities }`. The module
     * is NEVER loaded or executed in this slice — that is the Phase-3 plugin
     * runtime. `module_id` is an opaque handle, NOT a URL.
     */
    plugin: PluginSpecSchema,
  })
  .strict();

/**
 * A calendar definition — `structured` (the common path) or `plugin`
 * (#242). The two modes are a discriminated union on `mode`; a definition
 * with no `mode` key folds to `structured` (back-compat). `@dl/shared`'s
 * `pack.define_calendar` envelope carries the body opaquely; this schema is
 * the `@dl/calendar` structural chokepoint.
 *
 * The default-`structured` mode lives on the structured arm, so a raw
 * `{ id, name, cycles, … }` (no `mode`) parses as structured. A plugin
 * definition MUST set `mode: 'plugin'` explicitly.
 */
export const CalendarDefinitionSchema = z.union([
  PluginCalendarDefinitionSchema,
  StructuredCalendarDefinitionSchema,
]);

/**
 * A `mode: "plugin"` calendar definition (§3, #242). Carries a `plugin`
 * body and no structured fields.
 */
export type PluginCalendarDefinition = z.infer<typeof PluginCalendarDefinitionSchema>;

/**
 * A `mode: "structured"` calendar definition (§3) — the common path.
 *
 * `mode` is typed OPTIONAL (it `.default`s to `'structured'` at parse time)
 * so a definition authored before #242 — `{ id, name, cycles, … }` with no
 * `mode` key — assigns to this type unchanged. The structural fields stay
 * required; the existing consumers read `.cycles` / `.eras` directly.
 */
export type StructuredCalendarDefinition = Omit<
  z.infer<typeof StructuredCalendarDefinitionSchema>,
  'mode'
> & { mode?: 'structured' };

/**
 * A calendar definition — `structured` (the common path) or `plugin`
 * (#242), a discriminated union on `mode`. A definition with no `mode` key
 * is `structured` (the schema's `.default`); a plugin definition sets
 * `mode: 'plugin'` and omits the structured fields. Consumers narrow via
 * {@link isPluginCalendar} (or a bare `def.mode === 'plugin'` check) before
 * reading `.cycles`. `@dl/shared`'s `pack.define_calendar` envelope carries
 * the body opaquely; `CalendarDefinitionSchema` is the structural chokepoint
 * and the authority on which combinations are legal.
 */
export type CalendarDefinition = StructuredCalendarDefinition | PluginCalendarDefinition;

/**
 * Is this a plugin-mode calendar definition (§3, #242)? The single
 * predicate the client degrade path and any runtime consumer key on — a
 * plugin calendar LOADS (its pack's vocabulary stays available) but does
 * NOT render through the structured engine; calendar-dependent surfaces
 * show the "calendar not fully supported" notice instead.
 *
 * Accepts the loose `{ mode?, plugin? }` shape a folded definition carries
 * before `CalendarDefinitionSchema` re-validation, so the client can decide
 * to degrade WITHOUT first running the structural parse (which a plugin
 * definition would still pass, since plugin-mode omits the structured
 * fields). An explicit `mode: 'plugin'` is the signal; the default is
 * `structured`.
 */
export function isPluginCalendar(
  def: Readonly<Record<string, unknown>> | null | undefined,
): boolean {
  return def != null && def['mode'] === 'plugin';
}

/**
 * Project a `CalendarDefinition` to the runtime `Calendar` the
 * conversion engine consumes. The only structural difference is the
 * `name` (definition) ↔ `label` (runtime) field — the opaque `cal_` id
 * and the structural cycles/eras/leap/anchor carry through unchanged.
 *
 * #230 lands the dual-accepting `cycles` schema (legacy `{ year, week? }`
 * object OR the N-cycle bag); #231 (A2) makes the conversion engine
 * *interpret* the bag. A bag-form definition now narrows through to a
 * runtime `Calendar` by **projecting the bag into the legacy `{ year,
 * week }` arm** ({@link legacyCyclesFromBag}) — the runtime `Calendar`
 * type stays the narrow legacy shape every existing consumer reads
 * directly, while the bag's `month`/`year`/atomic-week cycles supply the
 * month sequence + weekday labels the leap-aware walk consumes.
 *
 * A bag that cannot be decomposed into dates (no `month` nested cycle
 * `based_on "day"`) is rejected here — eagerly, with a clear error —
 * rather than producing a calendar that throws deep inside the first
 * `toDisplayDate`. Legacy-arm definitions pass through unchanged.
 */
export function asCalendar(definition: CalendarDefinition): Calendar {
  if (definition.mode === 'plugin') {
    // A plugin calendar has no structured cycles to decompose; the runtime
    // `Calendar` cannot represent it. Callers MUST gate on `isPluginCalendar`
    // and degrade (the client renders a "calendar not fully supported"
    // notice) rather than reaching this. The throw is the eager failure for
    // a caller that ignored the gate — NO plugin code runs.
    throw new Error(
      `calendar '${definition.id}' is a plugin-mode calendar (no structured cycles); ` +
        'it cannot be projected to a runtime Calendar — gate on isPluginCalendar and degrade.',
    );
  }
  const { id, name, cycles, eras, leap_rule, anchor_canonical_day, anchor, formatter_override } = definition;
  const label: LocalizedString = name;
  const runtime: Calendar = {
    id,
    label,
    cycles: toLegacyCycles(cycles, id),
    eras,
    leap_rule,
    anchor_canonical_day,
  };
  if (anchor !== undefined) runtime.anchor = anchor;
  if (formatter_override !== undefined) runtime.formatter_override = formatter_override;
  return runtime;
}

/**
 * Narrow a definition's `cycles` to the runtime legacy `{ year, week? }`
 * arm. A legacy object passes through; a bag is projected via
 * {@link legacyCyclesFromBag}.
 */
function toLegacyCycles(cycles: Cycles, calendarId: string): LegacyCycles {
  if (!isCycleBag(cycles)) return cycles;
  const legacy = legacyCyclesFromBag(cycles);
  if (legacy === null) {
    throw new Error(
      `calendar '${calendarId}' declares a cycle bag with no resolvable 'month' nested cycle ` +
        '(a nested cycle based_on "day" with a per-instance length list is required to ' +
        'decompose a day into a display date).',
    );
  }
  return legacy;
}

/**
 * Project an N-cycle bag (A1, #230) into the runtime legacy `{ year,
 * week? }` arm the conversion engine consumes.
 *
 *   - `year.months` comes from the bag's `month` cycle — a nested cycle
 *     `based_on "day"` with an irregular per-instance length list. Each
 *     instance becomes a `MonthDefinition` (name from the instance name or
 *     the `list` label at that index; length from the pattern), preserving
 *     the festival/intercalary flag.
 *   - `week` comes from the FIRST atomic cycle carrying a `list` label
 *     (the conventional weekday cycle): the label list becomes the weekday
 *     labels, phase 0 (a hand-authored atomic cycle runs from day 0 —
 *     §4.1). A bag with no labeled atomic cycle yields a week-less calendar.
 *
 * Returns `null` when the bag has no decomposable `month` cycle.
 *
 * Leap variation is NOT encoded in the static bag (the bag is leap-unaware
 * by construction — see `cycles.ts`); a bag needing leap months ships
 * `leap_length`-bearing instances + a `leap_rule` exactly as a legacy
 * calendar does (the runtime walk reads `leap_length` off the projected
 * `MonthDefinition`). For the common no-leap bag this projection is exact.
 */
export function legacyCyclesFromBag(bag: CycleBag): LegacyCycles | null {
  const month = bag.find(
    (c) => c.name === 'month' && c.structure.kind === 'nested' && c.structure.based_on === 'day',
  );
  if (month === undefined || month.structure.kind !== 'nested') return null;
  const pattern = month.structure.pattern;
  if (pattern.kind !== 'irregular') return null;

  const monthLabels = listLabels(month.labels);
  const months: MonthDefinition[] = pattern.per_instance.map((inst, i) => {
    const name = inst.name ?? monthLabels?.[i] ?? `month_${i + 1}`;
    const def: MonthDefinition = { name, length: inst.length };
    if (inst.intercalary !== undefined) def.intercalary = inst.intercalary;
    return def;
  });

  const legacy: LegacyCycles = { year: { months } };
  for (const cycle of bag) {
    if (cycle.structure.kind === 'atomic' && cycle.labels?.kind === 'list') {
      return { ...legacy, week: { labels: cycle.labels.labels, phase: 0 } };
    }
  }
  return legacy;
}

/** The labels array of a `list` LabelSpec, or `undefined` otherwise. */
function listLabels(spec: LabelSpec | undefined): readonly string[] | undefined {
  if (spec === undefined || spec.kind !== 'list') return undefined;
  return spec.labels;
}
